import React, { useState, useRef, useEffect } from "react";
import { Button } from "./button";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Heading4,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Strikethrough,
  RotateCcw,
  RotateCw,
  Check,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface InlineWysiwygEditorProps {
  content: string;
  onChange: (html: string) => void;
  onSave?: () => void;
  onCancel?: () => void;
  className?: string;
  placeholder?: string;
}

/**
 * Custom WYSIWYG editor using contentEditable
 * Supports headers, bullets, bold, italic, and other basic formatting
 */
export const InlineWysiwygEditor: React.FC<InlineWysiwygEditorProps> = ({
  content,
  onChange,
  onSave,
  onCancel,
  className,
  placeholder = "Start typing...",
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showToolbar, setShowToolbar] = useState(true);
  const [, setSelectionUpdate] = useState(0); // Force re-render on selection change
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef<number>(-1);
  const pushDebounceRef = useRef<number | null>(null);
  const HISTORY_LIMIT = 50;
  const isLocalChangeRef = useRef(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize content on mount and when content prop changes (skip resets for local edits)
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== content) {
      editorRef.current.innerHTML = content;
    }
    if (isLocalChangeRef.current) {
      // This was triggered by our own onChange/undo/redo; don't reset history
      isLocalChangeRef.current = false;
      return;
    }
    // External change: reset revision history baseline
    historyRef.current = [content];
    historyIndexRef.current = 0;
    setHasUnsavedChanges(false);
  }, [content]);

  // Track selection changes to update toolbar button states
  useEffect(() => {
    const handleSelectionChange = () => {
      setSelectionUpdate(prev => prev + 1);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  // Handle content changes
  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      isLocalChangeRef.current = true;
      onChange(html);
      setHasUnsavedChanges(true);
      // Debounced snapshot push
      if (pushDebounceRef.current) window.clearTimeout(pushDebounceRef.current);
      pushDebounceRef.current = window.setTimeout(() => {
        const currentHistory = historyRef.current;
        const currentIndex = historyIndexRef.current;
        const last = currentHistory[currentIndex];
        if (last === html) return;
        const next = currentHistory.slice(0, currentIndex + 1);
        next.push(html);
        if (next.length > HISTORY_LIMIT) next.shift();
        historyRef.current = next;
        historyIndexRef.current = next.length - 1;
      }, 250);
    }
  };

  // Undo/redo helpers
  const canUndo = () => historyIndexRef.current > 0;
  const canRedo = () => historyIndexRef.current < historyRef.current.length - 1;

  const applyHtml = (html: string) => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = html;
    isLocalChangeRef.current = true;
    onChange(html);
    editorRef.current.focus();
  };

  const handleUndo = () => {
    if (!canUndo()) return;
    historyIndexRef.current -= 1;
    const html = historyRef.current[historyIndexRef.current] || "";
    applyHtml(html);
  };

  const handleRedo = () => {
    if (!canRedo()) return;
    historyIndexRef.current += 1;
    const html = historyRef.current[historyIndexRef.current] || "";
    applyHtml(html);
  };

  // Execute formatting command
  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  // Check if a formatting is active
  const isActive = (command: string, value?: string): boolean => {
    if (value) {
      return document.queryCommandValue(command) === value;
    }
    return document.queryCommandState(command);
  };

  // Get current block format tag
  const getCurrentBlockFormat = (): string => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return '';
    
    let node = selection.anchorNode;
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = (node as Element).tagName.toLowerCase();
        if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div'].includes(tagName)) {
          return tagName;
        }
      }
      node = node.parentNode;
    }
    return '';
  };

  // Custom heading commands with proper toggle
  const toggleHeading = (level: number) => {
    const tag = `h${level}`;
    const currentFormat = getCurrentBlockFormat();
    
    // If already in this heading level, convert back to paragraph
    if (currentFormat === tag) {
      document.execCommand('formatBlock', false, 'p');
    } else {
      document.execCommand('formatBlock', false, tag);
    }
    editorRef.current?.focus();
  };

  // Check if current selection is in a specific heading
  const isHeadingActive = (level: number): boolean => {
    return getCurrentBlockFormat() === `h${level}`;
  };

  // Handle link insertion
  const insertLink = () => {
    const url = prompt("Enter URL:");
    if (url) {
      execCommand('createLink', url);
    }
  };

  // Toolbar button component
  const ToolbarButton = ({
    icon: Icon,
    onClick,
    active = false,
    title,
    iconClassName,
  }: {
    icon: React.ElementType;
    onClick: () => void;
    active?: boolean;
    title: string;
    iconClassName?: string;
  }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={cn(
        "h-8 w-8 p-0",
        active && "bg-muted"
      )}
      title={title}
    >
      <Icon className={cn("h-4 w-4", iconClassName)} />
    </Button>
  );

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Floating Toolbar */}
      {showToolbar && (
        <div className="sticky top-0 z-10 bg-background border rounded-lg p-2 shadow-sm mb-2">
          <div className="flex flex-wrap gap-1 items-center">
            {/* Undo/Redo */}
            <ToolbarButton
              icon={RotateCcw}
              onClick={handleUndo}
              title="Undo (Ctrl/Cmd+Z)"
              iconClassName={!canUndo() ? 'opacity-40' : ''}
            />
            <ToolbarButton
              icon={RotateCw}
              onClick={handleRedo}
              title="Redo (Ctrl/Cmd+Shift+Z)"
              iconClassName={!canRedo() ? 'opacity-40' : ''}
            />

            {/* Text Formatting */}
            <ToolbarButton
              icon={Bold}
              onClick={() => execCommand('bold')}
              active={isActive('bold')}
              title="Bold (Ctrl+B)"
            />
            <ToolbarButton
              icon={Italic}
              onClick={() => execCommand('italic')}
              active={isActive('italic')}
              title="Italic (Ctrl+I)"
            />
            <ToolbarButton
              icon={Underline}
              onClick={() => execCommand('underline')}
              active={isActive('underline')}
              title="Underline (Ctrl+U)"
            />
            <ToolbarButton
              icon={Strikethrough}
              onClick={() => execCommand('strikeThrough')}
              active={isActive('strikeThrough')}
              title="Strikethrough"
            />

            <div className="w-px h-6 bg-border mx-1" />

            {/* Headings */}
            <ToolbarButton
              icon={Heading2}
              onClick={() => toggleHeading(2)}
              active={isHeadingActive(2)}
              title="Heading 2"
            />
            <ToolbarButton
              icon={Heading3}
              onClick={() => toggleHeading(3)}
              active={isHeadingActive(3)}
              title="Heading 3"
            />
            <ToolbarButton
              icon={Heading4}
              onClick={() => toggleHeading(4)}
              active={isHeadingActive(4)}
              title="Heading 4"
            />

            <div className="w-px h-6 bg-border mx-1" />

            {/* Lists */}
            <ToolbarButton
              icon={List}
              onClick={() => execCommand('insertUnorderedList')}
              active={isActive('insertUnorderedList')}
              title="Bullet List"
            />
            <ToolbarButton
              icon={ListOrdered}
              onClick={() => execCommand('insertOrderedList')}
              active={isActive('insertOrderedList')}
              title="Numbered List"
            />

            <div className="w-px h-6 bg-border mx-1" />

            {/* Alignment */}
            <ToolbarButton
              icon={AlignLeft}
              onClick={() => execCommand('justifyLeft')}
              title="Align Left"
            />
            <ToolbarButton
              icon={AlignCenter}
              onClick={() => execCommand('justifyCenter')}
              title="Align Center"
            />
            <ToolbarButton
              icon={AlignRight}
              onClick={() => execCommand('justifyRight')}
              title="Align Right"
            />

            <div className="w-px h-6 bg-border mx-1" />

            {/* Link */}
            <ToolbarButton
              icon={LinkIcon}
              onClick={insertLink}
              title="Insert Link"
            />

            {/* Action Buttons */}
            {(onSave || onCancel) && (
              <>
                <div className="w-px h-6 bg-border mx-1" />
                
                {onSave && (
                  hasUnsavedChanges ? (
                    <Button
                      type="button"
                      onClick={() => { onSave(); setHasUnsavedChanges(false); }}
                      className="h-8 px-3 bg-green-600 hover:bg-green-700 text-white gap-2"
                    >
                      <Check className="h-4 w-4" />
                      Save changes
                    </Button>
                  ) : (
                    <ToolbarButton
                      icon={Check}
                      onClick={onSave}
                      title="Save"
                      iconClassName="text-green-600 opacity-60"
                    />
                  )
                )}
                {onCancel && (
                  <ToolbarButton
                    icon={X}
                    onClick={onCancel}
                    title="Cancel"
                    iconClassName="text-red-600"
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Content Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={(e) => {
          const isMac = navigator.platform.toUpperCase().includes('MAC');
          const mod = isMac ? e.metaKey : e.ctrlKey;
          if (mod && e.key.toLowerCase() === 'z') {
            e.preventDefault();
            if (e.shiftKey) {
              handleRedo();
            } else {
              handleUndo();
            }
          }
        }}
        className={cn(
          "prose max-w-none focus:outline-none min-h-[400px] p-4",
          "border rounded-lg bg-background",
          "[&:empty]:before:content-[attr(data-placeholder)]",
          "[&:empty]:before:text-muted-foreground",
          "[&:empty]:before:pointer-events-none"
        )}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
};


